/**
 * MarketIntelligence Component
 * Collapsible section showing market data for tonight:
 * slate overview, top picks and team prop hit rates.
 * Gated per sub-section via useFeatureAccess.
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type {
  EnrichedTeamSignal,
  MLPropScore,
  SlateProfile,
} from '@parlay-picker/shared/api/mlApi';
import {
  getMlRecommendationDisplay,
  type MlRecommendationTone,
} from '@parlay-picker/shared/mlRecommendation';
import { cn } from '@utils/cn';
import { formatPropType } from '@utils/propUtils';
import { BarChart3, ChevronDown, Sparkles, TrendingUp } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { TeamSignalsCard } from './TeamSignalsCard';

// Only `bet` used to be green and everything else red, so a head that made no call read
// as an adverse one.
const ML_TONE_CLASS: Record<MlRecommendationTone, string> = {
  positive: 'bg-theme-success/20 text-theme-success',
  lean: 'bg-theme-info/20 text-theme-info',
  neutral: 'bg-theme-warning/20 text-theme-warning',
  negative: 'bg-theme-danger/20 text-theme-danger',
  none: 'bg-theme-text-tertiary/20 text-theme-text-tertiary',
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Sub-components
// =============================================================================

function LoadingSkeleton(): ReactElement {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-4 rounded bg-theme-border/30 animate-pulse" />
      ))}
    </div>
  );
}

function SlateOverviewCard({ data }: { data: SlateProfile }): ReactElement {
  const sizeColors: Record<string, string> = {
    light: 'bg-blue-500/20 text-blue-400',
    medium: 'bg-yellow-500/20 text-yellow-400',
    heavy: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-theme-text-secondary" />
        <span className="text-xs font-semibold text-theme-text uppercase tracking-wide">
          Slate Overview
        </span>
        <span
          className={cn(
            'text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase',
            sizeColors[data.size] ?? sizeColors.medium,
          )}
        >
          {data.size}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <div className="text-sm font-bold text-theme-text">{data.gameCount}</div>
          <div className="text-[10px] text-theme-text-tertiary">Games</div>
        </div>
        <div>
          <div className="text-sm font-bold text-theme-text">{data.propCount}</div>
          <div className="text-[10px] text-theme-text-tertiary">Props</div>
        </div>
        <div>
          <div className="text-sm font-bold text-theme-success">{data.betCount}</div>
          <div className="text-[10px] text-theme-text-tertiary">Bet</div>
        </div>
        <div>
          <div className="text-sm font-bold text-theme-text">{data.avgScore}</div>
          <div className="text-[10px] text-theme-text-tertiary">Avg Score</div>
        </div>
      </div>
    </div>
  );
}

const BINARY_PROP_TYPES = new Set(['double_double', 'triple_double']);
function isBinaryProp(propType: string): boolean {
  return BINARY_PROP_TYPES.has(propType);
}

function TopPicksTable({ picks }: { picks: MLPropScore[] }): ReactElement {
  // Deduplicate by player+propType (keep highest ML score per player/prop combo)
  const dedupedPicks = useMemo(() => {
    const seen = new Map<string, MLPropScore>();
    for (const pick of picks) {
      const key = `${(pick.playerName ?? '').toLowerCase()}|${pick.propType}`;
      const existing = seen.get(key);
      if (!existing || Number(pick.mlScore) > Number(existing.mlScore)) {
        seen.set(key, pick);
      }
    }
    return [...seen.values()].sort((a, b) => Number(b.mlScore) - Number(a.mlScore));
  }, [picks]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-theme-text-secondary" />
        <span className="text-xs font-semibold text-theme-text uppercase tracking-wide">
          Top ML Picks
        </span>
        <span className="text-[10px] text-theme-text-tertiary">({dedupedPicks.length} unique)</span>
      </div>
      {dedupedPicks.length === 0 ? (
        <p className="text-xs text-theme-text-tertiary">No ML scores available yet.</p>
      ) : (
        <div className="overflow-auto max-h-[280px] pr-1">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-theme-text-tertiary border-b border-theme-border/30">
                <th className="text-left py-1 pr-2 font-medium">Player</th>
                <th className="text-left py-1 pr-2 font-medium">Prop</th>
                <th className="text-left py-1 pr-2 font-medium">Dir</th>
                <th className="text-right py-1 pr-2 font-medium">Line</th>
                <th className="text-right py-1 pr-2 font-medium">Score</th>
                <th className="text-right py-1 font-medium">Rec</th>
              </tr>
            </thead>
            <tbody>
              {dedupedPicks.map((pick, i) => (
                <tr
                  key={`${pick.playerName}-${pick.propType}-${i}`}
                  className="border-b border-theme-border/10"
                >
                  <td className="py-1.5 pr-2 text-theme-text font-medium truncate max-w-[120px]">
                    {pick.playerName ?? 'Unknown'}
                  </td>
                  <td className="py-1.5 pr-2 text-theme-text-secondary">
                    {formatPropType(pick.propType)}
                  </td>
                  <td className="py-1.5 pr-2">
                    {pick.mlDirection ? (
                      <span
                        className={cn(
                          'text-[10px] font-bold uppercase',
                          pick.mlDirection === 'over' ? 'text-emerald-400' : 'text-orange-400',
                        )}
                      >
                        {pick.mlDirection === 'over' ? '↑ O' : '↓ U'}
                      </span>
                    ) : (
                      <span className="text-theme-text-tertiary">-</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-right text-theme-text-secondary">
                    {isBinaryProp(pick.propType)
                      ? pick.mlDirection === 'over'
                        ? 'Yes'
                        : 'No'
                      : (pick.line ?? '-')}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono font-bold text-theme-primary">
                    {Number(pick.mlScore).toFixed(0)}
                  </td>
                  <td className="py-1.5 text-right">
                    {pick.mlRecommendation
                      ? (() => {
                          const rec = getMlRecommendationDisplay(pick.mlRecommendation);
                          return (
                            <span
                              title={rec.description}
                              className={cn(
                                'text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase',
                                ML_TONE_CLASS[rec.tone],
                              )}
                            >
                              {rec.label}
                            </span>
                          );
                        })()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function MarketIntelligence(): ReactElement | null {
  const { authFetch } = useAuth();
  const { isHidden } = useFeatureAccess();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Data state
  const [slateProfile, setSlateProfile] = useState<SlateProfile | null>(null);
  const [topPicks, setTopPicks] = useState<MLPropScore[]>([]);
  const [teamSignals, setTeamSignals] = useState<EnrichedTeamSignal[] | null>(null);
  const [offensiveSignals, setOffensiveSignals] = useState<EnrichedTeamSignal[] | null>(null);
  const [signalView, setSignalView] = useState<'defensive' | 'offensive'>('defensive');

  // Don't render if all market features are hidden for this tier
  const allHidden =
    isHidden('ml_market_slate_overview') &&
    isHidden('ml_market_top_picks') &&
    isHidden('team_prop_hit_rates');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fetches: Promise<void>[] = [];

      // Slate profile
      if (!isHidden('ml_market_slate_overview')) {
        fetches.push(
          authFetch(apiUrl('/api/ml/slate-profile'))
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data) setSlateProfile(data as SlateProfile);
            })
            .catch(() => {}),
        );
      }

      // Top picks
      if (!isHidden('ml_market_top_picks')) {
        fetches.push(
          authFetch(apiUrl('/api/ml/top-picks'))
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data?.picks) setTopPicks(data.picks as MLPropScore[]);
            })
            .catch(() => {}),
        );
      }

      // Team signals (v2 — directional + per-prop) — defensive and offensive
      if (!isHidden('team_prop_hit_rates')) {
        fetches.push(
          authFetch(apiUrl('/api/ml/team-signals-v2'))
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data?.teams) setTeamSignals(data.teams as EnrichedTeamSignal[]);
            })
            .catch(() => {}),
          authFetch(apiUrl('/api/ml/team-offensive-signals'))
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data?.teams) setOffensiveSignals(data.teams as EnrichedTeamSignal[]);
            })
            .catch(() => {}),
        );
      }

      await Promise.all(fetches);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [authFetch, isHidden]);

  // Fetch when expanded for the first time
  useEffect(() => {
    if (!isCollapsed && !hasFetched) {
      fetchData();
    }
  }, [isCollapsed, hasFetched, fetchData]);

  if (allHidden) return null;

  return (
    <div className="rounded-xl border bg-theme-surface border-theme-border overflow-hidden">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => setIsCollapsed((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-theme-border/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-theme-text">Market Intelligence</span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 uppercase">
            Admin
          </span>
        </div>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-theme-text-tertiary transition-transform',
            !isCollapsed && 'rotate-180',
          )}
        />
      </button>

      {/* Content — collapsible */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-4 border-t border-theme-border/30">
          {loading && !hasFetched ? (
            <div className="pt-3">
              <LoadingSkeleton />
            </div>
          ) : (
            <>
              {/* Slate Overview */}
              {!isHidden('ml_market_slate_overview') && (
                <div className="pt-3">
                  {slateProfile ? (
                    <SlateOverviewCard data={slateProfile} />
                  ) : (
                    <p className="text-xs text-theme-text-tertiary pt-2">
                      No slate data available.
                    </p>
                  )}
                </div>
              )}

              {/* Top Picks */}
              {!isHidden('ml_market_top_picks') && (
                <div>
                  <TopPicksTable picks={topPicks} />
                </div>
              )}

              {/* Team Signals — Defensive vs Offensive toggle */}
              {!isHidden('team_prop_hit_rates') && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <button
                      type="button"
                      onClick={() => setSignalView('defensive')}
                      className={cn(
                        'text-[10px] font-semibold px-2 py-1 rounded-md transition-colors',
                        signalView === 'defensive'
                          ? 'bg-theme-primary/20 text-theme-primary'
                          : 'text-theme-text-tertiary hover:text-theme-text-secondary',
                      )}
                    >
                      Bet Against
                    </button>
                    <button
                      type="button"
                      onClick={() => setSignalView('offensive')}
                      className={cn(
                        'text-[10px] font-semibold px-2 py-1 rounded-md transition-colors',
                        signalView === 'offensive'
                          ? 'bg-theme-primary/20 text-theme-primary'
                          : 'text-theme-text-tertiary hover:text-theme-text-secondary',
                      )}
                    >
                      Bet On
                    </button>
                  </div>
                  {signalView === 'defensive' ? (
                    teamSignals ? (
                      <TeamSignalsCard teams={teamSignals} subtitle="prop hit rate vs opponent" />
                    ) : (
                      <p className="text-xs text-theme-text-tertiary">No team signals available.</p>
                    )
                  ) : offensiveSignals ? (
                    <TeamSignalsCard teams={offensiveSignals} subtitle="prop hit rate by team" />
                  ) : (
                    <p className="text-xs text-theme-text-tertiary">
                      No offensive signals available.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
